import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { enforce } from '../../../util/abac';
import { Helmet } from 'react-helmet';
import urls from '../../../util/urls';
import LoadingView from '../Loading';
import { useIntl, FormattedMessage } from 'react-intl';
import messages from './data/messages';
import { useSelector, useDispatch } from 'react-redux';
import {
  authSelectors,
  authActions,
} from '@lifeomic/phc-web-toolkit/src/redux/modules/auth';
import { getCookieByName } from '@lifeomic/phc-web-toolkit/src/redux/util/cookieUtils';
import { EXPIRES_COOKIE_NAME } from '@lifeomic/phc-web-toolkit/src/redux/modules/auth/actions';
import { useAuth, hasAuthParams } from 'react-oidc-context';
import { configSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/config';

const PrivateRoute = ({
  component: Component,
  authorized = true,
  location,
  intlId,
  title,
  project,
  account,
  policy,
  enforceAction,
  ...toPass
}) => {
  const { formatMessage } = useIntl();
  const auth = useAuth();
  const isAuthenticated = useSelector(authSelectors.isAuthenticated);
  const cookieState = useSelector(authSelectors.selectCookieState);
  const config = useSelector(state => configSelectors.selectConfigState(state));
  const dispatch = useDispatch();

  let renderRouteElement = () => null;

  const blockedByPolicy =
    !!enforceAction && !!policy && !enforce(enforceAction, policy);

  // Locally, we only do the OIDC flow and skip using a separate PHC Login
  // app instance.
  if (!isAuthenticated && !config.oidcAuthEnabled) {
    renderRouteElement = props => {
      window.location.href = urls.externalApps.login(window.location.href);
      return <LoadingView text={formatMessage(messages.redirectText)} />;
    };
  } else if (!authorized || blockedByPolicy) {
    renderRouteElement = () => <Redirect to={{ pathname: urls.app.home() }} />;
  } else {
    renderRouteElement = props => <Component {...props} {...toPass} />;
  }

  if (
    isAuthenticated &&
    cookieState?.isLoggedInWithCookie &&
    !getCookieByName(EXPIRES_COOKIE_NAME)
  ) {
    dispatch(authActions.logout());
  }

  React.useEffect(() => {
    if (
      config.oidcAuthEnabled &&
      config.oauthClientId &&
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.isLoading
    ) {
      auth.signinRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.oidcAuthEnabled,
    config.oauthClientId,
    auth.isAuthenticated,
    auth.activeNavigator,
    auth.isLoading,
    auth.signinRedirect,
  ]);

  return (
    <FormattedMessage
      id={intlId}
      defaultMessage={`${title} - ${project ? '{project} - ' : ''}${
        account ? '{account} - ' : ''
      }PHC`}
      values={{
        project,
        account,
      }}
    >
      {title => (
        <div style={{ position: 'relative', height: '100%', width: '100%' }}>
          <Helmet title={title}></Helmet>
          <Route {...toPass} render={renderRouteElement} />
        </div>
      )}
    </FormattedMessage>
  );
};

export default PrivateRoute;
